import { getInternalProblem } from './internalProblems';

const chapterThemes = {
  'binary-search': ['Boundary', 'Monotonic true/false', 'Midpoint discipline'],
  'arrays-and-hashing': ['Index fast paths', 'Frequency maps', 'Prefix state'],
  'two-pointer': ['Converging pointers', 'Sorted structure', 'Pair decisions'],
  'sliding-window': ['Window validity', 'Move right, repair left', 'Max/min templates'],
  'linked-list': ['Pointer rewiring', 'Dummy nodes', 'Fast and slow'],
  tree: ['Recursive shape', 'Traversal order', 'Return useful state'],
  trie: ['Prefix branches', 'Terminal nodes', 'Search pruning'],
  heap: ['Priority frontier', 'Top K', 'Streaming order'],
  backtracking: ['Choice tree', 'Undo state', 'Prune early'],
  graph: ['Visited state', 'Queue/stack frontier', 'Components and shortest paths'],
  'dynamic-programming': ['State definition', 'Transition', 'Memo/table order'],
  greedy: ['Local choice', 'Invariant', 'Exchange argument'],
  interval: ['Sort by boundary', 'Merge overlaps', 'Sweep events'],
  'monotonic-stack': ['Useful stack order', 'Next greater/smaller', 'Pop when resolved'],
  palindrome: ['Mirror center', 'Expand/check', 'DP symmetry'],
  'divide-and-conquer': ['Split', 'Solve halves', 'Merge answers'],
  'prefix-sum': ['Running total', 'Range difference', 'Hash prefix counts'],
  'sorted-list': ['Ordered window', 'Rank queries', 'Balanced operations'],
  'segment-tree': ['Range node', 'Combine results', 'Update path'],
  parenthesis: ['Balance', 'Stack pairs', 'Validity constraints'],
};

const representativeProblems = {
  'binary-search': ['sqrtx', 'find-peak-element', 'koko-eating-bananas'],
  'arrays-and-hashing': ['two-sum', 'group-anagrams', 'product-of-array-except-self'],
  'two-pointer': ['valid-palindrome', '3sum', 'container-with-most-water'],
  'sliding-window': ['longest-substring-without-repeating-characters', 'max-consecutive-ones-iii', 'sliding-window-maximum'],
  'linked-list': ['reverse-linked-list', 'middle-of-the-linked-list', 'copy-list-with-random-pointer'],
  tree: ['invert-binary-tree', 'validate-binary-search-tree', 'binary-tree-maximum-path-sum'],
  trie: ['implement-trie-prefix-tree', 'word-break', 'longest-common-prefix'],
  heap: ['kth-largest-element-in-an-array', 'k-closest-points-to-origin', 'find-median-from-data-stream'],
  backtracking: ['subsets', 'combination-sum', 'palindrome-partitioning'],
  graph: ['number-of-islands', 'clone-graph', 'course-schedule-ii'],
  'dynamic-programming': ['climbing-stairs', 'house-robber', 'coin-change'],
  greedy: ['jump-game', 'best-time-to-buy-and-sell-stock-ii', 'maximum-profit-in-job-scheduling'],
  interval: ['merge-intervals', 'insert-interval', 'interval-list-intersections'],
  'monotonic-stack': ['daily-temperatures', 'online-stock-span', 'largest-rectangle-in-histogram'],
  palindrome: ['longest-palindromic-substring', 'valid-palindrome', 'palindrome-partitioning'],
  'divide-and-conquer': ['maximum-subarray', 'kth-largest-element-in-an-array', 'merge-intervals'],
  'prefix-sum': ['subarray-sum-equals-k', 'range-sum-query-immutable', 'continuous-subarray-sum'],
  'sorted-list': ['find-median-from-data-stream', 'sliding-window-maximum', 'kth-largest-element-in-an-array'],
  'segment-tree': ['range-sum-query-immutable', 'count-number-of-nice-subarrays', 'sliding-window-maximum'],
  parenthesis: ['valid-parentheses', 'minimum-add-to-make-parentheses-valid', 'basic-calculator'],
};

const chapterVisualAssets = {
  'binary-search': '/generated/slides/binary-search.webp',
  'arrays-and-hashing': '/generated/slides/arrays-and-hashing.webp',
  'two-pointer': '/generated/slides/two-pointer.webp',
  'sliding-window': '/generated/slides/sliding-window.webp',
  'linked-list': '/generated/slides/linked-list.webp',
  tree: '/generated/slides/tree.webp',
  trie: '/generated/slides/trie.webp',
  heap: '/generated/slides/heap.webp',
  backtracking: '/generated/slides/backtracking.webp',
  graph: '/generated/slides/graph.webp',
  'dynamic-programming': '/generated/slides/dynamic-programming.webp',
  greedy: '/generated/slides/greedy.webp',
  interval: '/generated/slides/interval.webp',
  'monotonic-stack': '/generated/slides/monotonic-stack.webp',
  palindrome: '/generated/slides/palindrome.webp',
  'divide-and-conquer': '/generated/slides/divide-and-conquer.webp',
  'prefix-sum': '/generated/slides/prefix-sum.webp',
  'sorted-list': '/generated/slides/sorted-list.webp',
  'segment-tree': '/generated/slides/segment-tree.webp',
  parenthesis: '/generated/slides/parenthesis.webp',
};

const chapterLessonDetails = {
  'binary-search': {
    invariant: 'Maintain a search range where the answer is still possible. Move the side that cannot contain the final answer.',
    walkthrough: [
      ['left=0, right=x', 'The answer must be somewhere in this closed range.'],
      ['mid = floor((left + right) / 2)', 'Compare mid * mid against x.'],
      ['mid * mid <= x', 'mid is feasible, so store it and search right for a larger feasible value.'],
      ['mid * mid > x', 'mid is too large, so search left.'],
    ],
    code: `def solve(x):
    left, right = 0, x
    ans = 0

    while left <= right:
        mid = (left + right) // 2
        square = mid * mid

        if square <= x:
            ans = mid
            left = mid + 1
        else:
            right = mid - 1

    return ans`,
    complexity: 'Time O(log x), space O(1).',
    edgeCases: ['x = 0', 'x = 1', 'perfect squares', 'large non-square values near integer limits'],
  },
  'arrays-and-hashing': {
    invariant: 'Before processing index i, the hash map contains exactly the values from earlier indexes and the best lookup state so far.',
    walkthrough: [
      ['value = nums[i]', 'Ask what complement would finish the pair.'],
      ['need = target - value', 'If need was seen earlier, return those two indexes.'],
      ['store value -> i', 'Only store after checking so one element is not reused.'],
    ],
    code: `def solve(nums, target):
    seen = {}

    for i, value in enumerate(nums):
        need = target - value
        if need in seen:
            return [seen[need], i]
        seen[value] = i

    return []`,
    complexity: 'Time O(n), space O(n).',
    edgeCases: ['duplicate values', 'negative numbers', 'target formed by the same numeric value at two different indexes'],
  },
  'two-pointer': {
    invariant: 'The left and right pointers only move inward after their current characters have been fully handled.',
    walkthrough: [
      ['left at start, right at end', 'Skip non-alphanumeric characters on both sides.'],
      ['compare lowercase chars', 'A mismatch means the cleaned string cannot be a palindrome.'],
      ['move both pointers inward', 'Each successful comparison locks in one mirrored pair.'],
    ],
    code: `def solve(s):
    left, right = 0, len(s) - 1

    while left < right:
        while left < right and not s[left].isalnum():
            left += 1
        while left < right and not s[right].isalnum():
            right -= 1

        if s[left].lower() != s[right].lower():
            return False

        left += 1
        right -= 1

    return True`,
    complexity: 'Time O(n), space O(1).',
    edgeCases: ['empty after filtering', 'mixed case', 'digits', 'punctuation-heavy strings'],
  },
  'sliding-window': {
    invariant: 'The current window contains no duplicate characters after the repair loop finishes.',
    walkthrough: [
      ['expand right', 'Add the next character to the active window.'],
      ['repair while duplicate', 'Move left until the window is valid again.'],
      ['update best', 'The valid window length is right - left + 1.'],
    ],
    code: `def solve(s):
    seen = set()
    left = 0
    best = 0

    for right, char in enumerate(s):
        while char in seen:
            seen.remove(s[left])
            left += 1
        seen.add(char)
        best = max(best, right - left + 1)

    return best`,
    complexity: 'Time O(n), space O(min(n, alphabet)).',
    edgeCases: ['empty string', 'all unique', 'all same character', 'duplicate reappearing after the left pointer moves'],
  },
  'dynamic-programming': {
    invariant: 'Each state answers one smaller version of the problem, and later states only depend on already solved states.',
    walkthrough: [
      ['dp[0] = 1, dp[1] = 1', 'Base cases define the smallest valid subproblems.'],
      ['dp[i] = dp[i-1] + dp[i-2]', 'The last move came from one step below or two steps below.'],
      ['roll variables', 'Only the previous two states are needed.'],
    ],
    code: `def solve(n):
    if n <= 1:
        return 1

    prev2, prev1 = 1, 1
    for _ in range(2, n + 1):
        curr = prev1 + prev2
        prev2, prev1 = prev1, curr

    return prev1`,
    complexity: 'Time O(n), space O(1).',
    edgeCases: ['n = 0', 'n = 1', 'small values before optimizing space'],
  },
  graph: {
    invariant: 'Every cell is visited at most once, and a search consumes one full connected component.',
    walkthrough: [
      ['scan every cell', 'A fresh land cell starts a new island.'],
      ['DFS/BFS from that cell', 'Mark all connected land as visited.'],
      ['increment once per component', 'Do not count individual cells.'],
    ],
    code: `def solve(grid):
    if not grid:
        return 0

    rows, cols = len(grid), len(grid[0])
    islands = 0

    def dfs(r, c):
        if r < 0 or c < 0 or r == rows or c == cols or grid[r][c] != "1":
            return
        grid[r][c] = "0"
        dfs(r + 1, c)
        dfs(r - 1, c)
        dfs(r, c + 1)
        dfs(r, c - 1)

    for r in range(rows):
        for c in range(cols):
            if grid[r][c] == "1":
                islands += 1
                dfs(r, c)

    return islands`,
    complexity: 'Time O(rows * cols), space O(rows * cols) in the worst recursion case.',
    edgeCases: ['empty grid', 'all water', 'all land', 'diagonal land does not connect'],
  },
  parenthesis: {
    invariant: 'The stack contains the unmatched opening brackets in the exact order they must be closed.',
    walkthrough: [
      ['opening bracket', 'Push it because it needs a future match.'],
      ['closing bracket', 'It must match the most recent opening bracket.'],
      ['end of string', 'Valid only if nothing remains unmatched.'],
    ],
    code: `def solve(s):
    pairs = {")": "(", "]": "[", "}": "{"}
    stack = []

    for char in s:
        if char in pairs.values():
            stack.append(char)
        elif not stack or stack.pop() != pairs[char]:
            return False

    return not stack`,
    complexity: 'Time O(n), space O(n).',
    edgeCases: ['odd length strings', 'closing bracket first', 'nested brackets', 'same count but wrong order'],
  },
  'linked-list': {
    invariant: 'At every step, prev points to the reversed prefix and curr points to the first node not yet reversed.',
    walkthrough: [
      ['prev = None, curr = head', 'Nothing has been reversed yet.'],
      ['next_node = curr.next', 'Save the rest of the list before rewiring curr.next.'],
      ['curr.next = prev', 'Move the current node onto the front of the reversed prefix.'],
      ['prev = curr; curr = next_node', 'Advance both regions until curr is null.'],
    ],
    code: `def solve(head):
    prev = None
    curr = head

    while curr:
        next_node = curr.next
        curr.next = prev
        prev = curr
        curr = next_node

    return prev`,
    complexity: 'Time O(n), space O(1).',
    edgeCases: ['empty list', 'single node', 'two nodes', 'remembering to save next before overwriting curr.next'],
  },
  tree: {
    invariant: 'Each recursive call returns the fully solved answer for its subtree before the parent combines those results.',
    walkthrough: [
      ['visit node', 'Decide whether this traversal needs preorder, inorder, or postorder.'],
      ['solve left and right', 'Let recursion give you trustworthy subtree answers.'],
      ['combine at parent', 'Return exactly the state the parent needs, not extra work.'],
    ],
    code: `def solve(root):
    if not root:
        return None

    left = solve(root.left)
    right = solve(root.right)

    root.left = right
    root.right = left
    return root`,
    complexity: 'Time O(n), space O(h), where h is tree height.',
    edgeCases: ['empty tree', 'single node', 'skewed tree', 'distinguishing subtree return value from global answer'],
  },
  trie: {
    invariant: 'Every node represents one prefix, and terminal markers distinguish complete words from prefixes.',
    walkthrough: [
      ['start at root', 'The root represents the empty prefix.'],
      ['follow/create char edge', 'Each character moves one level deeper in the prefix tree.'],
      ['mark terminal', 'A complete word needs a marker even if longer words share the same path.'],
      ['search prefix vs word', 'Prefix search stops at the node; word search also checks terminal.'],
    ],
    code: `class TrieNode:
    def __init__(self):
        self.children = {}
        self.is_word = False


class Trie:
    def __init__(self):
        self.root = TrieNode()

    def insert(self, word):
        node = self.root
        for char in word:
            node = node.children.setdefault(char, TrieNode())
        node.is_word = True

    def search(self, word):
        node = self.root
        for char in word:
            if char not in node.children:
                return False
            node = node.children[char]
        return node.is_word`,
    complexity: 'Insert/search take O(L) time for word length L; space is O(total inserted characters).',
    edgeCases: ['word is prefix of another word', 'duplicate inserts', 'empty string policy', 'prefix exists but full word does not'],
  },
  heap: {
    invariant: 'The heap keeps the next most important candidate at the top while all other candidates remain cheaply updateable.',
    walkthrough: [
      ['push candidate', 'Store the priority first, then any tie-break fields.'],
      ['heap size exceeds k', 'Remove the candidate that should not survive in the top-k set.'],
      ['heap top after scan', 'The top is the boundary item, such as kth largest.'],
    ],
    code: `import heapq


def solve(nums, k):
    heap = []

    for value in nums:
        heapq.heappush(heap, value)
        if len(heap) > k:
            heapq.heappop(heap)

    return heap[0]`,
    complexity: 'Time O(n log k), space O(k).',
    edgeCases: ['k = 1', 'k equals array length', 'duplicates', 'max-heap simulation by negating values'],
  },
  backtracking: {
    invariant: 'The path contains exactly the decisions made so far; every recursive return must undo the last decision.',
    walkthrough: [
      ['choose', 'Add one valid candidate to the path.'],
      ['explore', 'Recurse with updated start/index/state.'],
      ['unchoose', 'Remove the candidate so sibling branches start clean.'],
      ['base case', 'Copy the path when it represents a complete answer.'],
    ],
    code: `def solve(nums):
    res = []
    path = []

    def backtrack(start):
        res.append(path[:])
        for i in range(start, len(nums)):
            path.append(nums[i])
            backtrack(i + 1)
            path.pop()

    backtrack(0)
    return res`,
    complexity: 'Time O(n * 2^n), space O(n) recursion depth excluding output.',
    edgeCases: ['copy path before storing', 'duplicate candidates', 'correct next index', 'undoing every mutation'],
  },
  greedy: {
    invariant: 'The local choice must preserve the possibility of an optimal global answer; track the proof as a state variable.',
    walkthrough: [
      ['scan current position', 'Update the farthest reachable index.'],
      ['if current index exceeds farthest', 'The path is impossible because no earlier choice can reach here.'],
      ['finish scan', 'If every index is reachable, the greedy frontier was sufficient.'],
    ],
    code: `def solve(nums):
    farthest = 0

    for i, jump in enumerate(nums):
        if i > farthest:
            return False
        farthest = max(farthest, i + jump)

    return True`,
    complexity: 'Time O(n), space O(1).',
    edgeCases: ['first value is zero', 'single element', 'large early jump', 'getting stuck before the final index'],
  },
  interval: {
    invariant: 'After sorting, the merged list contains non-overlapping intervals covering everything processed so far.',
    walkthrough: [
      ['sort by start', 'Adjacent overlaps become local decisions.'],
      ['new interval starts after current end', 'No overlap, so append it as a new block.'],
      ['otherwise overlap', 'Extend the current block end if needed.'],
    ],
    code: `def solve(intervals):
    intervals.sort()
    merged = []

    for start, end in intervals:
        if not merged or start > merged[-1][1]:
            merged.append([start, end])
        else:
            merged[-1][1] = max(merged[-1][1], end)

    return merged`,
    complexity: 'Time O(n log n) for sorting, space O(n) for output.',
    edgeCases: ['touching endpoints', 'nested intervals', 'already sorted input', 'empty list'],
  },
  'monotonic-stack': {
    invariant: 'The stack stores unresolved indexes in an order that makes the next greater or smaller answer immediate when it appears.',
    walkthrough: [
      ['scan new value', 'It may resolve one or more previous indexes.'],
      ['pop while condition breaks', 'Each popped item has found its next useful neighbor.'],
      ['push current index', 'Current index is now unresolved and waits for a future value.'],
    ],
    code: `def solve(temperatures):
    ans = [0] * len(temperatures)
    stack = []

    for i, temp in enumerate(temperatures):
        while stack and temperatures[stack[-1]] < temp:
            j = stack.pop()
            ans[j] = i - j
        stack.append(i)

    return ans`,
    complexity: 'Time O(n), space O(n); each index is pushed and popped at most once.',
    edgeCases: ['strictly decreasing input', 'strictly increasing input', 'equal values', 'storing indexes instead of values'],
  },
  palindrome: {
    invariant: 'Every accepted expansion has matching characters on both sides of the center.',
    walkthrough: [
      ['pick a center', 'Each palindrome is centered at one character or between two characters.'],
      ['expand while equal', 'Grow outward only while the mirror condition holds.'],
      ['update best/count', 'The last valid expansion gives a candidate answer.'],
    ],
    code: `def solve(s):
    best = ""

    def expand(left, right):
        nonlocal best
        while left >= 0 and right < len(s) and s[left] == s[right]:
            if right - left + 1 > len(best):
                best = s[left:right + 1]
            left -= 1
            right += 1

    for i in range(len(s)):
        expand(i, i)
        expand(i, i + 1)

    return best`,
    complexity: 'Time O(n^2), space O(1) beyond the output substring.',
    edgeCases: ['even-length palindromes', 'single character', 'all same characters', 'case/character filtering when required'],
  },
  'divide-and-conquer': {
    invariant: 'Each recursive call solves a smaller range, and the merge step combines two correct partial answers.',
    walkthrough: [
      ['split range', 'Stop when the subarray has size one.'],
      ['solve left and right', 'Trust each half to return sorted output.'],
      ['merge two sorted halves', 'Repeatedly take the smaller front item.'],
    ],
    code: `def solve(nums):
    if len(nums) <= 1:
        return nums

    mid = len(nums) // 2
    left = solve(nums[:mid])
    right = solve(nums[mid:])

    merged = []
    i = j = 0
    while i < len(left) or j < len(right):
        if j == len(right) or (i < len(left) and left[i] <= right[j]):
            merged.append(left[i])
            i += 1
        else:
            merged.append(right[j])
            j += 1
    return merged`,
    complexity: 'Time O(n log n), space O(n).',
    edgeCases: ['empty input', 'odd lengths', 'duplicates', 'merge loop leftovers'],
  },
  'prefix-sum': {
    invariant: 'prefix stores the sum before the current position, so range sums become differences between two states.',
    walkthrough: [
      ['prefix = 0', 'No elements have been consumed.'],
      ['add current value', 'Update the running total after each element.'],
      ['need = prefix - k', 'Previous prefixes with this value form a subarray sum of k.'],
      ['count current prefix', 'Make it available for future ranges.'],
    ],
    code: `from collections import defaultdict


def solve(nums, k):
    counts = defaultdict(int)
    counts[0] = 1
    prefix = 0
    total = 0

    for value in nums:
        prefix += value
        total += counts[prefix - k]
        counts[prefix] += 1

    return total`,
    complexity: 'Time O(n), space O(n).',
    edgeCases: ['subarray starts at index 0', 'negative numbers', 'zero values', 'multiple matching previous prefixes'],
  },
  'sorted-list': {
    invariant: 'The ordered window always contains exactly the active items, so min/max/rank queries are local operations.',
    walkthrough: [
      ['insert new value in sorted order', 'Maintain ordering as the window expands.'],
      ['remove expired value', 'Keep the data structure synchronized with the window bounds.'],
      ['query ends or neighbors', 'Use sorted order to answer range, median, or limit checks.'],
    ],
    code: `import bisect


def solve(nums, limit):
    window = []
    left = 0
    best = 0

    for right, value in enumerate(nums):
        bisect.insort(window, value)
        while window[-1] - window[0] > limit:
            idx = bisect.bisect_left(window, nums[left])
            window.pop(idx)
            left += 1
        best = max(best, right - left + 1)

    return best`,
    complexity: 'With a balanced sorted list: O(n log n). With Python list insertion/removal: O(n^2) worst case.',
    edgeCases: ['duplicate values', 'removing the correct duplicate', 'window shrinking multiple times', 'large windows needing a real SortedList'],
  },
  'segment-tree': {
    invariant: 'Every tree node stores the aggregate for one range, and parent values are the combination of their children.',
    walkthrough: [
      ['build leaves', 'Each leaf represents one original array value.'],
      ['build parents', 'Combine left and right child aggregates.'],
      ['query range', 'Use full-cover nodes and split partial overlaps.'],
      ['update leaf', 'Change one value, then recompute ancestors.'],
    ],
    code: `class NumArray:
    def __init__(self, nums):
        self.n = len(nums)
        self.tree = [0] * (2 * self.n)
        for i, value in enumerate(nums):
            self.tree[self.n + i] = value
        for i in range(self.n - 1, 0, -1):
            self.tree[i] = self.tree[2 * i] + self.tree[2 * i + 1]

    def update(self, index, val):
        i = index + self.n
        self.tree[i] = val
        while i > 1:
            i //= 2
            self.tree[i] = self.tree[2 * i] + self.tree[2 * i + 1]

    def sumRange(self, left, right):
        left += self.n
        right += self.n
        total = 0
        while left <= right:
            if left % 2 == 1:
                total += self.tree[left]
                left += 1
            if right % 2 == 0:
                total += self.tree[right]
                right -= 1
            left //= 2
            right //= 2
        return total`,
    complexity: 'Build O(n), query O(log n), update O(log n), space O(n).',
    edgeCases: ['single element arrays', 'inclusive vs exclusive query bounds', 'point updates after queries', 'choosing the correct combine function'],
  },
};

const chapterWorkedExamples = {
  'binary-search': [
    {
      title: 'Worked example: Sqrt(x)',
      caption: 'Trace x = 26 as a monotonic feasibility search. Values whose square is <= 26 are valid; the goal is the largest valid value.',
      kind: 'number-line',
      subject: 'x = 26',
      values: Array.from({ length: 14 }, (_, value) => value),
      finalAnswer: '5',
      legend: ['blue = current mid', 'green = feasible', 'red = too large'],
      steps: [
        {
          label: 'Start with the full answer range',
          left: 0,
          right: 13,
          mid: null,
          ans: 0,
          status: 'unknown',
          note: 'For x = 26, any answer above 13 cannot matter because 13 * 13 already exceeds 26.',
        },
        {
          label: 'Try mid = 6',
          left: 0,
          right: 13,
          mid: 6,
          ans: 0,
          status: 'too-high',
          note: '6 * 6 = 36, which is too large. Move right to mid - 1.',
        },
        {
          label: 'Try mid = 2',
          left: 0,
          right: 5,
          mid: 2,
          ans: 2,
          status: 'feasible',
          note: '2 * 2 = 4 is feasible. Save 2 and search right for a larger feasible value.',
        },
        {
          label: 'Try mid = 4',
          left: 3,
          right: 5,
          mid: 4,
          ans: 4,
          status: 'feasible',
          note: '4 * 4 = 16 is feasible. Save 4 and keep pushing right.',
        },
        {
          label: 'Try mid = 5',
          left: 5,
          right: 5,
          mid: 5,
          ans: 5,
          status: 'feasible',
          note: '5 * 5 = 25 is feasible, then the range crosses. The integer square root is 5.',
        },
      ],
    },
    {
      title: 'Worked example: Koko Eating Bananas',
      caption: 'Search the minimum eating speed. A speed is feasible when the total hours is <= h.',
      kind: 'koko',
      subject: 'piles = [3, 6, 7, 11], h = 8',
      piles: [3, 6, 7, 11],
      speeds: Array.from({ length: 11 }, (_, index) => index + 1),
      finalAnswer: '4 bananas/hour',
      legend: ['blue = tested speed', 'green = can finish', 'red = too slow'],
      steps: [
        {
          label: 'Start with speed range 1..11',
          left: 1,
          right: 11,
          mid: null,
          hours: null,
          ans: null,
          status: 'unknown',
          note: 'The slowest possible speed is 1. The fastest speed worth checking is the largest pile, 11.',
        },
        {
          label: 'Try speed = 6',
          left: 1,
          right: 11,
          mid: 6,
          hours: 6,
          ans: 6,
          status: 'feasible',
          note: 'ceil(3/6)+ceil(6/6)+ceil(7/6)+ceil(11/6) = 6 hours. Feasible, so search lower speeds.',
        },
        {
          label: 'Try speed = 3',
          left: 1,
          right: 5,
          mid: 3,
          hours: 10,
          ans: 6,
          status: 'too-low',
          note: '10 hours is too slow. Move left to mid + 1.',
        },
        {
          label: 'Try speed = 4',
          left: 4,
          right: 5,
          mid: 4,
          hours: 8,
          ans: 4,
          status: 'feasible',
          note: '8 hours is exactly allowed. Save 4 and still check if an even smaller speed works.',
        },
        {
          label: 'Try speed = 3 again? No, range is done',
          left: 4,
          right: 3,
          mid: null,
          hours: 8,
          ans: 4,
          status: 'done',
          note: 'The search crossed after proving speeds below 4 are too slow. Minimum speed is 4.',
        },
      ],
    },
  ],
  'arrays-and-hashing': [
    {
      title: 'Worked example: Two Sum',
      caption: 'Trace nums = [2, 7, 11, 15], target = 9. The hash map stores earlier values so each new value can ask whether its complement already exists.',
      kind: 'concept',
      subject: 'nums = [2, 7, 11, 15], target = 9',
      finalAnswer: '[0, 1]',
      legend: ['scan current value', 'lookup complement', 'store seen values'],
      steps: [
        { label: 'Start empty', state: 'seen = {}', value: 'target 9', status: 'unknown', note: 'Before scanning, no previous value can form a pair.' },
        { label: 'i = 0, value = 2', state: 'need = 7', value: '{}', status: 'unknown', note: '7 is not in seen, so store 2 -> 0.' },
        { label: 'i = 1, value = 7', state: 'need = 2', value: '{2: 0}', status: 'feasible', note: '2 was seen at index 0. Current index 1 completes the target.' },
        { label: 'Return pair', state: 'answer', value: '[0, 1]', status: 'done', note: 'Return indices, not values: nums[0] + nums[1] = 9.' },
      ],
    },
  ],
  'two-pointer': [
    {
      title: 'Worked example: Container With Most Water',
      caption: 'Use the shorter wall to decide which pointer can move without missing a better area.',
      kind: 'concept',
      subject: 'height = [1, 8, 6, 2, 5, 4, 8, 3, 7]',
      finalAnswer: '49',
      legend: ['left pointer', 'right pointer', 'move shorter side'],
      steps: [
        { label: 'Start wide', state: 'l=0, r=8', value: 'area 8', status: 'unknown', note: 'The widest container uses height min(1, 7), so area is 8.' },
        { label: 'Move left', state: 'l=1, r=8', value: 'area 49', status: 'feasible', note: 'The left wall was shorter, so moving right inward could never improve it. Move left to height 8.' },
        { label: 'Save best', state: 'best=49', value: 'min(8,7)*7', status: 'feasible', note: 'This is the best candidate so far.' },
        { label: 'Continue inward', state: 'shorter side moves', value: 'r--', status: 'done', note: 'The invariant is that only the shorter wall can be the limiting factor worth replacing.' },
      ],
    },
  ],
  'sliding-window': [
    {
      title: 'Worked example: Longest Substring Without Repeating Characters',
      caption: 'Trace s = "abcabcbb". Expand right, then repair left whenever the window contains a duplicate.',
      kind: 'concept',
      subject: 's = "abcabcbb"',
      finalAnswer: '3',
      legend: ['expand right', 'repair duplicate', 'record valid length'],
      steps: [
        { label: 'Read abc', state: 'window = abc', value: 'best = 3', status: 'feasible', note: 'The first three characters are unique, so best becomes 3.' },
        { label: 'Duplicate a', state: 'abca', value: 'invalid', status: 'too-high', note: 'The second a breaks the invariant: the active window must contain no duplicates.' },
        { label: 'Move left', state: 'bca', value: 'valid again', status: 'feasible', note: 'Remove characters from the left until the duplicate a is gone.' },
        { label: 'Finish scan', state: 'best remains 3', value: 'abc/bca/cab', status: 'done', note: 'Several windows reach length 3, but none exceed it.' },
      ],
    },
  ],
  'linked-list': [
    {
      title: 'Worked example: Reverse Linked List',
      caption: 'Reverse 1 -> 2 -> 3 by separating the reversed prefix from the unreversed suffix.',
      kind: 'concept',
      subject: 'head = [1, 2, 3]',
      finalAnswer: '[3, 2, 1]',
      legend: ['prev = reversed prefix', 'curr = next node', 'save next before rewiring'],
      steps: [
        { label: 'Initialize', state: 'prev=null, curr=1', value: '1 -> 2 -> 3', status: 'unknown', note: 'Nothing is reversed yet.' },
        { label: 'Rewire 1', state: '1 -> null', value: 'prev=1, curr=2', status: 'feasible', note: 'Save 2 first, then point 1.next back to prev.' },
        { label: 'Rewire 2', state: '2 -> 1 -> null', value: 'curr=3', status: 'feasible', note: 'The reversed prefix grows by one node.' },
        { label: 'Rewire 3', state: '3 -> 2 -> 1', value: 'curr=null', status: 'done', note: 'When curr is null, prev is the new head.' },
      ],
    },
  ],
  tree: [
    {
      title: 'Worked example: Invert Binary Tree',
      caption: 'The recursive call inverts each subtree, then the parent swaps its children.',
      kind: 'concept',
      subject: 'root = [4,2,7,1,3,6,9]',
      finalAnswer: '[4,7,2,9,6,3,1]',
      legend: ['postorder recursion', 'swap children', 'return subtree root'],
      steps: [
        { label: 'Visit 4', state: 'root', value: 'left=2, right=7', status: 'unknown', note: 'Ask both subtrees to invert themselves first.' },
        { label: 'Invert left subtree', state: '2', value: 'children 3,1', status: 'feasible', note: 'Node 2 swaps its children after recursively solving them.' },
        { label: 'Invert right subtree', state: '7', value: 'children 9,6', status: 'feasible', note: 'Node 7 follows the same rule.' },
        { label: 'Swap at 4', state: '4', value: 'left=7, right=2', status: 'done', note: 'The whole tree is inverted when the root swaps solved subtrees.' },
      ],
    },
  ],
  trie: [
    {
      title: 'Worked example: Insert and Search',
      caption: 'Insert "apple", then distinguish searching the full word from checking a prefix.',
      kind: 'concept',
      subject: 'insert("apple"), search("app")',
      finalAnswer: 'false before app is inserted',
      legend: ['character edge', 'terminal marker', 'prefix vs word'],
      steps: [
        { label: 'Start at root', state: 'root', value: '{}', status: 'unknown', note: 'The root represents the empty prefix.' },
        { label: 'Create path', state: 'a -> p -> p -> l -> e', value: 'children', status: 'feasible', note: 'Each character creates or follows one child edge.' },
        { label: 'Mark terminal', state: 'e.isWord = true', value: 'apple', status: 'feasible', note: 'Only the final node marks a complete inserted word.' },
        { label: 'Search app', state: 'app.isWord = false', value: 'false', status: 'done', note: 'The prefix exists, but it is not a full word until app is inserted.' },
      ],
    },
  ],
  heap: [
    {
      title: 'Worked example: Kth Largest Element',
      caption: 'Keep a min heap of size k. The heap top is the kth largest after scanning all values.',
      kind: 'concept',
      subject: 'nums = [3,2,1,5,6,4], k = 2',
      finalAnswer: '5',
      legend: ['push value', 'trim to k', 'heap top is answer'],
      steps: [
        { label: 'Push 3,2', state: 'heap=[2,3]', value: 'size 2', status: 'feasible', note: 'The heap stores the two largest values seen so far.' },
        { label: 'Read 1', state: 'push then pop', value: 'heap=[2,3]', status: 'unknown', note: '1 cannot survive in the top 2.' },
        { label: 'Read 5,6', state: 'heap=[5,6]', value: 'trim smaller', status: 'feasible', note: 'The heap keeps only the best k candidates.' },
        { label: 'Read 4', state: 'heap=[5,6]', value: 'top=5', status: 'done', note: 'The smallest value in the top-2 set is the 2nd largest overall.' },
      ],
    },
  ],
  backtracking: [
    {
      title: 'Worked example: Subsets',
      caption: 'For nums = [1,2,3], each recursion level chooses whether to extend the current path.',
      kind: 'concept',
      subject: 'nums = [1, 2, 3]',
      finalAnswer: '8 subsets',
      legend: ['choose', 'explore', 'unchoose'],
      steps: [
        { label: 'Start path', state: '[]', value: 'record []', status: 'unknown', note: 'Every path is a valid subset, including the empty set.' },
        { label: 'Choose 1', state: '[1]', value: 'explore after index 0', status: 'feasible', note: 'Add 1 and recurse on later choices.' },
        { label: 'Choose 2 then 3', state: '[1,2,3]', value: 'record', status: 'feasible', note: 'The branch reaches a complete path.' },
        { label: 'Backtrack', state: 'pop choices', value: 'siblings', status: 'done', note: 'Undo each choice so sibling branches start from clean state.' },
      ],
    },
  ],
  graph: [
    {
      title: 'Worked example: Number of Islands',
      caption: 'A BFS/DFS consumes one connected component of land at a time.',
      kind: 'concept',
      subject: 'grid with connected 1-cells',
      finalAnswer: 'component count',
      legend: ['scan cells', 'start search', 'mark visited'],
      steps: [
        { label: 'Scan grid', state: 'find unvisited 1', value: 'new island', status: 'unknown', note: 'Only fresh land cells start a new component.' },
        { label: 'Flood fill', state: 'DFS/BFS', value: 'neighbors', status: 'feasible', note: 'Visit all four-directionally connected land.' },
        { label: 'Mark water', state: 'visited', value: 'do not recount', status: 'feasible', note: 'Mutating to 0 or using a visited set prevents double counting.' },
        { label: 'Continue scan', state: 'islands += 1 per search', value: 'answer', status: 'done', note: 'The answer counts components, not cells.' },
      ],
    },
  ],
  'dynamic-programming': [
    {
      title: 'Worked example: Coin Change',
      caption: 'Build dp[amount] from smaller amounts. Each coin proposes one transition.',
      kind: 'concept',
      subject: 'coins = [1,2,5], amount = 11',
      finalAnswer: '3',
      legend: ['state', 'transition', 'minimum'],
      steps: [
        { label: 'Define state', state: 'dp[x]', value: 'min coins for x', status: 'unknown', note: 'The state answers one amount, not the entire problem.' },
        { label: 'Base case', state: 'dp[0] = 0', value: 'zero coins', status: 'feasible', note: 'Amount 0 needs no coins.' },
        { label: 'Transition', state: 'dp[x] = 1 + dp[x-coin]', value: 'try each coin', status: 'feasible', note: 'Only use transitions where x - coin is nonnegative.' },
        { label: 'Read dp[11]', state: '5+5+1', value: '3', status: 'done', note: 'The minimum coin count for amount 11 is 3.' },
      ],
    },
  ],
  greedy: [
    {
      title: 'Worked example: Jump Game',
      caption: 'Track the farthest reachable index. If the scan ever passes it, the end is impossible.',
      kind: 'concept',
      subject: 'nums = [2,3,1,1,4]',
      finalAnswer: 'true',
      legend: ['current index', 'farthest reach', 'never get stuck'],
      steps: [
        { label: 'Start', state: 'farthest=0', value: 'i=0', status: 'unknown', note: 'Index 0 is reachable by definition.' },
        { label: 'At i=0', state: 'farthest=max(0,2)', value: '2', status: 'feasible', note: 'From index 0, we can reach index 2.' },
        { label: 'At i=1', state: 'farthest=max(2,4)', value: '4', status: 'feasible', note: 'Index 1 is reachable, and it reaches the end.' },
        { label: 'Finish', state: 'all scanned positions reachable', value: 'true', status: 'done', note: 'The greedy frontier never falls behind the scan.' },
      ],
    },
  ],
  interval: [
    {
      title: 'Worked example: Merge Intervals',
      caption: 'Sort by start so every overlap can be decided against the current merged interval.',
      kind: 'concept',
      subject: '[[1,3],[2,6],[8,10],[15,18]]',
      finalAnswer: '[[1,6],[8,10],[15,18]]',
      legend: ['sort by start', 'compare starts', 'extend end'],
      steps: [
        { label: 'Sort intervals', state: '[1,3], [2,6], ...', value: 'ordered', status: 'unknown', note: 'Sorting makes possible overlaps adjacent.' },
        { label: 'Compare [2,6]', state: '2 <= 3', value: 'overlap', status: 'feasible', note: 'The next start lands inside the current merged range.' },
        { label: 'Extend', state: '[1, max(3,6)]', value: '[1,6]', status: 'feasible', note: 'Merge by extending the end.' },
        { label: 'Append gaps', state: '[8,10], [15,18]', value: 'no overlap', status: 'done', note: 'Intervals that start after the current end become new blocks.' },
      ],
    },
  ],
  'monotonic-stack': [
    {
      title: 'Worked example: Daily Temperatures',
      caption: 'Store unresolved days in decreasing temperature order. A warmer day resolves older colder days.',
      kind: 'concept',
      subject: 'temperatures = [73,74,75,71,69,72,76,73]',
      finalAnswer: '[1,1,4,2,1,1,0,0]',
      legend: ['unresolved stack', 'pop when warmer', 'distance is answer'],
      steps: [
        { label: 'Day 0', state: 'stack=[0]', value: '73 unresolved', status: 'unknown', note: 'No future warmer day has appeared yet.' },
        { label: 'Day 1 warmer', state: '74 > 73', value: 'ans[0]=1', status: 'feasible', note: 'Pop day 0 and record the distance.' },
        { label: 'Cooler stretch', state: '75,71,69', value: 'stack grows', status: 'unknown', note: 'Colder days wait until a warmer value appears.' },
        { label: 'Day 6 = 76', state: 'pop many', value: 'resolve 5,3,2', status: 'done', note: 'One warm day can resolve several previous days.' },
      ],
    },
  ],
  palindrome: [
    {
      title: 'Worked example: Longest Palindromic Substring',
      caption: 'Every palindrome expands from a center. Try odd and even centers.',
      kind: 'concept',
      subject: 's = "babad"',
      finalAnswer: '"bab" or "aba"',
      legend: ['choose center', 'expand mirrors', 'save longest'],
      steps: [
        { label: 'Center at a', state: 'b a b', value: 'mirrors match', status: 'feasible', note: 'Expanding around index 1 finds "bab".' },
        { label: 'Try wider', state: 'out of match', value: 'stop', status: 'unknown', note: 'Expansion stops when characters differ or bounds end.' },
        { label: 'Center at b', state: 'a b a', value: 'also length 3', status: 'feasible', note: 'Another valid longest palindrome appears.' },
        { label: 'Return best', state: 'length 3', value: 'bab/aba', status: 'done', note: 'Either longest string is accepted.' },
      ],
    },
  ],
  'divide-and-conquer': [
    {
      title: 'Worked example: Merge Sort',
      caption: 'Split until single elements, then merge sorted halves back together.',
      kind: 'concept',
      subject: 'nums = [5,2,3,1]',
      finalAnswer: '[1,2,3,5]',
      legend: ['split', 'solve halves', 'merge'],
      steps: [
        { label: 'Split', state: '[5,2] | [3,1]', value: 'halves', status: 'unknown', note: 'Divide the range into smaller independent subproblems.' },
        { label: 'Split again', state: '[5] [2] [3] [1]', value: 'base cases', status: 'feasible', note: 'Single-element arrays are already sorted.' },
        { label: 'Merge halves', state: '[2,5] and [1,3]', value: 'sorted halves', status: 'feasible', note: 'The join step combines two solved subproblems.' },
        { label: 'Final merge', state: '[1,2,3,5]', value: 'sorted', status: 'done', note: 'The answer emerges from repeated correct joins.' },
      ],
    },
  ],
  'prefix-sum': [
    {
      title: 'Worked example: Subarray Sum Equals K',
      caption: 'A subarray sum is the difference between two prefix sums.',
      kind: 'concept',
      subject: 'nums = [1,2,3], k = 3',
      finalAnswer: '2',
      legend: ['running prefix', 'lookup prefix-k', 'count matches'],
      steps: [
        { label: 'prefix = 0', state: 'counts={0:1}', value: 'base', status: 'unknown', note: 'The empty prefix allows subarrays starting at index 0.' },
        { label: 'Read 1', state: 'prefix=1', value: 'need=-2', status: 'unknown', note: 'No previous prefix creates sum 3 yet.' },
        { label: 'Read 2', state: 'prefix=3', value: 'need=0', status: 'feasible', note: 'A previous prefix 0 means [1,2] sums to 3.' },
        { label: 'Read 3', state: 'prefix=6', value: 'need=3', status: 'done', note: 'Previous prefix 3 means [3] also sums to 3, total 2.' },
      ],
    },
  ],
  'sorted-list': [
    {
      title: 'Worked example: Longest Continuous Subarray',
      caption: 'Maintain a sorted window so min and max are always available.',
      kind: 'concept',
      subject: 'nums = [8,2,4,7], limit = 4',
      finalAnswer: '2',
      legend: ['insert sorted', 'compare max-min', 'remove expired left'],
      steps: [
        { label: 'Window [8]', state: 'min=max=8', value: 'valid', status: 'feasible', note: 'A one-element window always satisfies the limit.' },
        { label: 'Add 2', state: 'max-min=6', value: 'invalid', status: 'too-high', note: 'The difference exceeds 4, so shrink from the left.' },
        { label: 'Window [2,4]', state: 'diff=2', value: 'best=2', status: 'feasible', note: 'Sorted order makes min/max checks immediate.' },
        { label: 'Add 7', state: '[4,7]', value: 'best=2', status: 'done', note: 'After repair, no valid window longer than 2 appears.' },
      ],
    },
  ],
  'segment-tree': [
    {
      title: 'Worked example: Range Sum Query',
      caption: 'A segment tree answers range queries by combining complete-cover nodes.',
      kind: 'concept',
      subject: 'nums = [-2,0,3,-5,2,-1], query [0,2]',
      finalAnswer: '1',
      legend: ['range node', 'complete cover', 'combine sums'],
      steps: [
        { label: 'Build leaves', state: 'tree leaves = nums', value: 'base', status: 'unknown', note: 'Each leaf stores one array value.' },
        { label: 'Build parents', state: 'sum children', value: 'aggregates', status: 'feasible', note: 'Each parent stores the sum of its range.' },
        { label: 'Query [0,2]', state: 'take covered nodes', value: '-2 + 0 + 3', status: 'feasible', note: 'Use only nodes fully inside the requested range.' },
        { label: 'Combine', state: 'sum = 1', value: 'answer', status: 'done', note: 'The query returns the combined aggregate.' },
      ],
    },
  ],
  parenthesis: [
    {
      title: 'Worked example: Valid Parentheses',
      caption: 'Closing brackets must match the most recent unmatched opening bracket.',
      kind: 'concept',
      subject: 's = "({[]})"',
      finalAnswer: 'true',
      legend: ['push opener', 'match closer', 'stack empty at end'],
      steps: [
        { label: 'Read openers', state: 'stack = (, {, [', value: 'push', status: 'unknown', note: 'Each opener waits for a matching closer.' },
        { label: 'Read ]', state: 'top = [', value: 'match', status: 'feasible', note: 'The closer matches the most recent opener, so pop it.' },
        { label: 'Read } and )', state: 'pop { then (', value: 'nested order', status: 'feasible', note: 'Closers must unwind in reverse order.' },
        { label: 'End', state: 'stack empty', value: 'true', status: 'done', note: 'All openers were matched correctly.' },
      ],
    },
  ],
};

const palette = [
  'linear-gradient(135deg, #eef4ff 0%, #cfe5ff 44%, #eef1ff 100%)',
  'linear-gradient(135deg, #f7fbff 0%, #dff7f2 48%, #e9e6ff 100%)',
  'linear-gradient(135deg, #f8fbff 0%, #e7e9ff 42%, #d7f4ff 100%)',
];

function genericWorkedExample(chapter, chapterId, detail, primaryProblem, firstCase, points) {
  const sampleInput = firstCase?.input ?? {};
  const sampleOutput = firstCase?.expected ?? firstCase?.output ?? null;
  return {
    type: 'worked-example',
    title: `Interactive trace: ${primaryProblem.title}`,
    caption: `Step through a representative ${chapter?.title || 'pattern'} problem using the same invariant you will reuse in practice.`,
    kind: 'concept',
    subject: primaryProblem.title,
    finalAnswer: JSON.stringify(sampleOutput),
    legend: points,
    steps: [
      {
        label: 'Read the concrete input',
        state: 'Input',
        value: JSON.stringify(sampleInput),
        status: 'unknown',
        note: 'Start by naming the shape of the input before choosing a template.',
      },
      ...detail.walkthrough.map(([state, reason], index) => ({
        label: state,
        state,
        value: points[index % points.length] || chapterId,
        status: index === 0 ? 'mid' : index === detail.walkthrough.length - 1 ? 'feasible' : 'unknown',
        note: reason,
      })),
      {
        label: 'Return the checked result',
        state: 'Output',
        value: JSON.stringify(sampleOutput),
        status: 'done',
        note: `The trace should explain why the expected output is ${JSON.stringify(sampleOutput)}.`,
      },
    ],
  };
}

const chapterIdAliases = {
  binarySearch: 'binary-search',
  arraysAndHashing: 'arrays-and-hashing',
  twoPointer: 'two-pointer',
  slidingWindow: 'sliding-window',
  linkedList: 'linked-list',
  dynamicProgramming: 'dynamic-programming',
  monotonicStack: 'monotonic-stack',
  divideAndConquer: 'divide-and-conquer',
  prefixSum: 'prefix-sum',
  sortedList: 'sorted-list',
  segmentTree: 'segment-tree',
};

function normalizeChapterId(id) {
  return chapterIdAliases[id] || id;
}

export function getChapterSlides(chapter) {
  const chapterId = normalizeChapterId(chapter?.id);
  const points = chapterThemes[chapterId] || ['Core signal', 'Reusable template', 'Practice loop'];
  const problemSlugs = representativeProblems[chapterId] || ['two-sum', 'valid-parentheses', 'best-time-to-buy-and-sell-stock'];
  const problems = problemSlugs.map((slug) => getInternalProblem(slug));
  const primaryProblem = problems[0];
  const firstCase = primaryProblem?.testCases?.[0];
  const detail = chapterLessonDetails[chapterId] || {
    invariant: 'Define the state that stays true after every operation, then write code that preserves it.',
    walkthrough: [
      ['Read the input shape', 'Identify the pattern signal before choosing a template.'],
      ['Write the invariant', 'The invariant tells you which updates are legal.'],
      ['Trace the sample', 'Every line of code should be justified by the trace.'],
    ],
    code: primaryProblem?.starterCode?.python || 'def solve():\n    pass',
    complexity: 'State the time and space costs after the invariant is clear.',
    edgeCases: ['empty or minimum input', 'duplicates', 'boundary values'],
  };
  const sectionTitles = chapter?.sections?.slice(0, 3).map((section) => section.title) || [];
  const slides = [
    {
      type: 'bullets',
      title: `${chapter?.title || 'Pattern'} overview`,
      caption: 'Start with the core signals, then connect them to a worked problem and reusable implementation pattern.',
      bullets: points,
    },
    {
      type: 'bullets',
      title: 'Recognition signals',
      caption: `Reach for this pattern when the problem hints at ${points.map((point) => point.toLowerCase()).join(', ')}.`,
      bullets: points.map((point) => `Signal: ${point}`),
    },
    {
      type: 'diagram',
      title: 'Pattern diagram',
      caption: detail.invariant,
      imageSrc: chapterVisualAssets[chapterId],
      diagram: points,
      bullets: points,
    },
    ...((chapterWorkedExamples[chapterId] || [genericWorkedExample(chapter, chapterId, detail, primaryProblem, firstCase, points)]).map((example) => ({
      type: 'worked-example',
      ...example,
    }))),
    {
      type: 'problem',
      title: `Worked problem: ${primaryProblem.title}`,
      caption: primaryProblem.prompt,
      problem: primaryProblem,
      bullets: [
        `Difficulty: ${primaryProblem.difficulty}`,
        `Input: ${JSON.stringify(firstCase?.input ?? {})}`,
        `Expected: ${JSON.stringify(firstCase?.expected ?? firstCase?.output ?? null)}`,
      ],
    },
    {
      type: 'table',
      title: 'Dry run the sample',
      caption: 'Track the state changes first. The code should follow the same transitions.',
      rows: detail.walkthrough,
      bullets: detail.walkthrough.map(([state, reason]) => `${state}: ${reason}`),
    },
    {
      type: 'code',
      title: 'Code walkthrough',
      caption: detail.invariant,
      code: detail.code,
      bullets: sectionTitles.length ? sectionTitles : ['State', 'Transition', 'Boundary'],
    },
    {
      type: 'bullets',
      title: 'Complexity and edge cases',
      caption: detail.complexity,
      bullets: detail.edgeCases,
    },
    {
      type: 'bullets',
      title: 'Practice set',
      caption: 'Solve these next and compare how the same invariant appears in different forms.',
      bullets: problems.map((problem) => `${problem.title} (${problem.difficulty})`),
    },
    {
      type: 'bullets',
      title: 'Pattern checklist',
      caption: 'Use the same checklist before coding each new problem in this chapter.',
      bullets: ['Recognize the signal', 'Write the invariant', 'Trace one sample', 'Code the template', 'Test the edge cases'],
    },
  ];

  return slides.map((slide, index) => ({
    ...slide,
    background: palette[index % palette.length],
    imagePrompt: `Image Gen 2.0: simple premium abstract educational visual for ${chapter?.title || 'coding patterns'} showing ${slide.title}, soft white/blue UI palette, no text.`,
  }));
}
